// src/note-categories.ts
var NOTE_TYPES = {
  SESSION: "session",
  CHARACTER: "character",
  USER_NOTE: "user_note",
  EVENT: "event",
  NPC: "npc",
  ITEM: "item",
  MAP: "map",
  FACTION: "faction",
  LOCATION: "location",
  CREATURE: "creature",
  QUEST: "quest",
  LORE: "lore",
  RELATION: "relation",
  DOCUMENT: "document"
};
var EXTRACTABLE_NOTE_TYPES = [
  NOTE_TYPES.EVENT,
  NOTE_TYPES.NPC,
  NOTE_TYPES.CHARACTER,
  NOTE_TYPES.LOCATION,
  NOTE_TYPES.ITEM,
  NOTE_TYPES.CREATURE,
  NOTE_TYPES.FACTION,
  NOTE_TYPES.QUEST,
  NOTE_TYPES.LORE,
  NOTE_TYPES.RELATION
];
function isExtractableNoteType(type) {
  return EXTRACTABLE_NOTE_TYPES.includes(type);
}
function normalizeNoteType(type) {
  if (!type) {
    return NOTE_TYPES.EVENT;
  }
  const normalized = type.toLowerCase().trim();
  if (isExtractableNoteType(normalized)) {
    return normalized;
  }
  return NOTE_TYPES.EVENT;
}
var NOTE_CATEGORIES_CONFIG = [
  {
    id: "session",
    label: "Session",
    description: "Game session recordings and transcriptions",
    color: "bg-purple-100 dark:bg-purple-900/50 text-purple-800 dark:text-purple-200"
  },
  {
    id: "story",
    label: "Story",
    description: "Main narrative events and plot developments",
    color: "bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-200"
  },
  {
    id: "exploration",
    label: "Exploration",
    description: "Discovery, travel, and exploration events",
    color: "bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-200"
  },
  {
    id: "combat",
    label: "Combat",
    description: "Battles, fights, and combat encounters",
    color: "bg-red-100 dark:bg-red-900/50 text-red-800 dark:text-red-200"
  },
  {
    id: "social",
    label: "Social",
    description: "Social interactions, dialogues, and negotiations",
    color: "bg-yellow-100 dark:bg-yellow-900/50 text-yellow-900 dark:text-yellow-200"
  },
  {
    id: "character",
    label: "Character",
    description: "Character development and personal arcs",
    color: "bg-indigo-100 dark:bg-indigo-900/50 text-indigo-800 dark:text-indigo-200"
  },
  {
    id: "mechanics",
    label: "Mechanics",
    description: "Game rules, mechanics, and system notes",
    color: "bg-gray-100 dark:bg-gray-900/50 text-gray-800 dark:text-gray-200"
  },
  {
    id: "magic",
    label: "Magic",
    description: "Magical events, spells, and arcane occurrences",
    color: "bg-violet-100 dark:bg-violet-900/50 text-violet-800 dark:text-violet-200"
  },
  {
    id: "lore",
    label: "Lore",
    description: "World history, legends, and background information",
    color: "bg-amber-100 dark:bg-amber-900/50 text-amber-900 dark:text-amber-200"
  },
  {
    id: "location",
    label: "Location",
    description: "Places, maps, and geographical information",
    color: "bg-teal-100 dark:bg-teal-900/50 text-teal-800 dark:text-teal-200"
  },
  {
    id: "npc",
    label: "NPC",
    description: "Non-player characters and their details",
    color: "bg-orange-100 dark:bg-orange-900/50 text-orange-900 dark:text-orange-200"
  },
  {
    id: "item",
    label: "Item",
    description: "Items, equipment, and treasures",
    color: "bg-cyan-100 dark:bg-cyan-900/50 text-cyan-800 dark:text-cyan-200"
  },
  {
    id: "quest",
    label: "Quest",
    description: "Missions, objectives, and quest information",
    color: "bg-rose-100 dark:bg-rose-900/50 text-rose-800 dark:text-rose-200"
  },
  {
    id: "faction",
    label: "Faction",
    description: "Organizations, guilds, and political groups",
    color: "bg-slate-100 dark:bg-slate-900/50 text-slate-800 dark:text-slate-200"
  },
  {
    id: "religion",
    label: "Religion",
    description: "Deities, faiths, and religious institutions",
    color: "bg-fuchsia-100 dark:bg-fuchsia-900/50 text-fuchsia-800 dark:text-fuchsia-200"
  },
  {
    id: "encounter",
    label: "Encounter",
    description: "Specific encounters including traps and puzzles",
    color: "bg-red-100 dark:bg-red-900/50 text-red-800 dark:text-red-200"
  },
  {
    id: "creature",
    label: "Creature",
    description: "Monsters, beasts, and creature information",
    color: "bg-lime-100 dark:bg-lime-900/50 text-lime-900 dark:text-lime-200"
  },
  {
    id: "secret",
    label: "Secret",
    description: "Hidden information and GM secrets",
    color: "bg-zinc-100 dark:bg-zinc-900/50 text-zinc-800 dark:text-zinc-200"
  },
  {
    id: "prophecy",
    label: "Prophecy",
    description: "Prophecies, visions, and omens",
    color: "bg-violet-100 dark:bg-violet-900/50 text-violet-800 dark:text-violet-200"
  },
  {
    id: "timeline",
    label: "Timeline",
    description: "Historical events and chronology",
    color: "bg-sky-100 dark:bg-sky-900/50 text-sky-800 dark:text-sky-200"
  },
  {
    id: "relationship",
    label: "Relationship",
    description: "Character relationships and alliances",
    color: "bg-pink-100 dark:bg-pink-900/50 text-pink-800 dark:text-pink-200"
  },
  {
    id: "recap",
    label: "Recap",
    description: "Session summaries and recaps",
    color: "bg-emerald-100 dark:bg-emerald-900/50 text-emerald-800 dark:text-emerald-200"
  },
  {
    id: "handout",
    label: "Handout",
    description: "Player handouts and reference materials",
    color: "bg-stone-100 dark:bg-stone-900/50 text-stone-800 dark:text-stone-200"
  },
  {
    id: "rules",
    label: "Rules",
    description: "House rules and custom game rules",
    color: "bg-neutral-100 dark:bg-neutral-900/50 text-neutral-800 dark:text-neutral-200"
  },
  {
    id: "culture",
    label: "Culture",
    description: "Customs, traditions, and cultural information",
    color: "bg-amber-100 dark:bg-amber-900/50 text-amber-900 dark:text-amber-200"
  },
  {
    id: "document",
    label: "Document",
    description: "Uploaded documents and reference materials",
    color: "bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-200"
  }
];
var NOTE_CATEGORIES = NOTE_CATEGORIES_CONFIG.map(
  (c) => c.id
);
function getCategoryConfig(id) {
  return NOTE_CATEGORIES_CONFIG.find((c) => c.id === id);
}
function normalizeCategory(category) {
  if (!category) {
    return null;
  }
  const normalized = category.toLowerCase().trim();
  const match = NOTE_CATEGORIES_CONFIG.find((c) => c.id === normalized);
  return match ? match.id : null;
}
function getSystemCategoriesForPrompt() {
  return NOTE_CATEGORIES_CONFIG.map((c) => `- ${c.id}`).join("\n");
}
function getSystemCategoriesWithDescriptions() {
  return NOTE_CATEGORIES_CONFIG.map((c) => `- ${c.id}: ${c.description}`).join(
    "\n"
  );
}

export {
  NOTE_TYPES,
  EXTRACTABLE_NOTE_TYPES,
  isExtractableNoteType,
  normalizeNoteType,
  NOTE_CATEGORIES_CONFIG,
  NOTE_CATEGORIES,
  getCategoryConfig,
  normalizeCategory,
  getSystemCategoriesForPrompt,
  getSystemCategoriesWithDescriptions
};
//# sourceMappingURL=chunk-6HH4ASIL.js.map